/**
 * SoleadLab 2.0 - Project Sync Service
 *
 * Uploads all local blob assets to R2 and saves project JSON to D1.
 * Called explicitly by the user via "Save to Server" button.
 */

import { uploadAsset, uploadLargeAsset } from './assetApi';
import { saveProject, fetchProject } from './projectApi';
import {
  createEntityOverlay,
  createEntityOrthophoto,
  updateEntityOverlay,
  updateEntityOrthophoto,
} from './spatialEntityApi';
import { updateEntityIndication } from './indicationApi';
import { getAllPhotoBlobs } from '../utils/photoBlobs';
import { getAllSourceFiles, removeSourceFile } from '../utils/sourceFiles';
import { requestTileGeneration } from './tileApi';
import { useMonitoringStore } from '../stores/monitoringStore';
import { useProjectStore } from '../stores/projectStore';
import type { ProjectData } from '../stores/projectStore';
import type { SiteLayout } from '../domain/layout/types';

/**
 * Sync entire project to server:
 * 1. Upload all blob: URLs (overlays, orthophotos, photos) to R2
 * 2. Replace blob URLs with R2 URLs in a cloned ProjectData
 * 3. Save the updated JSON to D1
 * 4. Return the updated ProjectData (with R2 URLs)
 */
export async function syncProjectToServer(
  siteId: string,
  data: ProjectData,
  onProgress?: (current: number, total: number, msg: string) => void
): Promise<ProjectData> {
  console.log('[Sync] Starting sync for siteId:', siteId);
  console.log('[Sync] Data keys:', Object.keys(data));
  console.log('[Sync] Data size:', JSON.stringify(data).length, 'bytes');

  // Wait for any in-flight entity creates/updates kicked off by add* /
  // update* actions to settle, so we read fresh _version values before
  // issuing further PATCHes. Without this, a save fired right after
  // dropping an orthophoto would race the createEntityOrthophoto promise
  // and skip the URL/tile-info PATCH (silent no-op on undefined version).
  await useProjectStore.getState().flushPendingEntityWrites();

  // Re-export from the store after the flush — getConfirmedOrthophotos /
  // getConfirmedOverlays no longer mask freshly-created entities, and
  // _version values are settled. The `data` parameter is now stale.
  const freshData = useProjectStore.getState().exportProject();

  // Clone so we don't mutate the store directly
  const updated: ProjectData = JSON.parse(JSON.stringify(freshData));

  // Collect all assets that need uploading (blob: URLs)
  interface PendingUpload {
    type: 'overlay' | 'orthophoto' | 'photo' | 'geo_photo' | 'layout_image' | 'layout_pdf';
    index: number;
    blobUrl: string;
    name: string;
    /** For type 'photo': which IndicationProperties image field this blob fills. */
    field?: 'image' | 'visualImage' | 'zoomImage';
  }
  const pending: PendingUpload[] = [];

  // Overlays with blob URLs
  updated.overlays.forEach((ov, i) => {
    if (ov.url.startsWith('blob:')) {
      pending.push({ type: 'overlay', index: i, blobUrl: ov.url, name: ov.name.includes('.') ? ov.name : ov.name + '.png' });
    }
  });

  // Orthophotos with blob URLs
  updated.orthophotos.forEach((op, i) => {
    if (op.url.startsWith('blob:')) {
      pending.push({ type: 'orthophoto', index: i, blobUrl: op.url, name: (op.name || `orthophoto_${i}`) + '.png' });
    }
  });

  // Indications with photo blob paths. The primary `image` plus any paired
  // `visualImage`/`zoomImage` (from local thermal/visual/zoom upload — F-04A)
  // are stored as "photos/xxx" relPaths or blob: URLs and must be uploaded to
  // R2 so they survive a reload. Drive-imported visual/zoom are http(s) URLs and
  // are skipped here (already persisted/external).
  const photoBlobs = getAllPhotoBlobs();
  const PHOTO_FIELDS = ['image', 'visualImage', 'zoomImage'] as const;
  updated.indications.forEach((ind, i) => {
    for (const field of PHOTO_FIELDS) {
      const imgPath = ind.properties?.[field];
      if (!imgPath || imgPath.startsWith('http')) continue;
      if (imgPath.startsWith('blob:')) {
        // Direct blob URL (e.g. from ZIP import without siteId)
        pending.push({ type: 'photo', index: i, field, blobUrl: imgPath, name: `photo_${field}_${ind.id}.jpg` });
      } else {
        // Relative path in photoBlobs map
        const entry = photoBlobs.get(imgPath);
        if (entry) {
          pending.push({ type: 'photo', index: i, field, blobUrl: entry.url, name: imgPath.split('/').pop() || `photo_${field}_${i}.jpg` });
        }
      }
    }
  });

  // Geo photos with blob URLs
  updated.geoPhotos.forEach((gp, i) => {
    if (gp.url.startsWith('blob:')) {
      pending.push({ type: 'geo_photo', index: i, blobUrl: gp.url, name: `geo_${gp.id}.jpg` });
    }
  });

  // Site layout assets with blob URLs.
  //
  // F-01A: prefer the canonical `siteLayouts[]` array. The legacy `siteLayout`
  // singleton is treated as a length-1 fallback for projects that have not yet
  // been normalized through `loadProject`. `index` is the position inside the
  // siteLayouts array so the asset-replace switch below can update the right
  // entry; the singleton fallback uses index 0 (which is also where the alias
  // points after normalization).
  const layoutsForUpload: SiteLayout[] =
    updated.siteLayouts && updated.siteLayouts.length > 0
      ? updated.siteLayouts
      : (updated.siteLayout ? [updated.siteLayout] : []);
  for (let li = 0; li < layoutsForUpload.length; li++) {
    const l = layoutsForUpload[li];
    if (l.pageImage?.url.startsWith('blob:')) {
      pending.push({ type: 'layout_image', index: li, blobUrl: l.pageImage.url, name: `layout_${l.id}.png` });
    }
    if (l.pdfAssetUrl?.startsWith('blob:')) {
      pending.push({ type: 'layout_pdf', index: li, blobUrl: l.pdfAssetUrl, name: `layout_${l.id}.pdf` });
    }
  }

  const total = pending.length;
  console.log('[Sync] Pending blob uploads:', total);
  if (total === 0) {
    onProgress?.(0, 0, 'No local assets to upload');
  }

  // F-04A: accumulate per-indication photo-field URL rewrites (image /
  // visualImage / zoomImage) during the upload loop, then PATCH each affected
  // indication ONCE afterwards — one indication may have all three fields
  // uploaded, and three chained PATCHes would race _version → 412.
  const photoUrlRewrites = new Map<number, Record<string, string>>();

  // Upload each asset
  for (let idx = 0; idx < pending.length; idx++) {
    const item = pending[idx];
    onProgress?.(idx + 1, total, `Uploading ${item.name}...`);

    try {
      // Fetch the blob from the object URL
      let blob: Blob;
      if (item.type === 'photo') {
        const relPath = item.field ? updated.indications[item.index].properties?.[item.field] : undefined;
        const entry = relPath ? photoBlobs.get(relPath) : undefined;
        if (entry?.file) {
          blob = entry.file;
        } else {
          blob = await fetch(item.blobUrl).then((r) => r.blob());
        }
      } else {
        blob = await fetch(item.blobUrl).then((r) => r.blob());
      }

      const result = await uploadAsset(blob, siteId, item.type, item.name);

      // Replace blob URL with R2 URL in the cloned data + persist to entity API
      switch (item.type) {
        case 'overlay': {
          updated.overlays[item.index].url = result.url;
          // V4 P2.7: Persist R2 URL to entity table (entity API owns overlay writes).
          // If the entity row doesn't exist yet (overlay added before siteId was
          // set, or addOverlay's create promise failed), POST it now with the
          // R2 URL inline; otherwise PATCH the URL onto the existing row.
          const ov = updated.overlays[item.index];
          try {
            let serverOv;
            if (ov._version) {
              serverOv = await updateEntityOverlay(siteId, ov.id, { url: result.url }, ov._version);
            } else {
              serverOv = await createEntityOverlay(siteId, { ...ov, url: result.url });
            }
            updated.overlays[item.index]._version = serverOv._version;
            // Update local store state directly (NOT via updateOverlay action,
            // which would re-trigger entity API → double-write + version conflict)
            useProjectStore.setState((state) => ({
              data: {
                ...state.data,
                overlays: state.data.overlays.map((o) =>
                  o.id === ov.id ? { ...o, url: result.url, _version: serverOv._version } : o
                ),
              },
            }));
          } catch (err) {
            console.warn(`[Sync] Entity overlay persist failed for ${ov.id}:`, err);
          }
          break;
        }
        case 'orthophoto': {
          updated.orthophotos[item.index].url = result.url;
          // V4 P2.7: Persist R2 URL to entity table (entity API owns orthophoto writes).
          // If the entity row doesn't exist yet (orthophoto added before siteId
          // was set in the CreateProjectModal flow, or addOrthophoto's create
          // never fired because siteId was null), POST it now with the R2 URL
          // inline; otherwise PATCH the URL onto the existing row.
          const op = updated.orthophotos[item.index];
          try {
            let serverOp;
            if (op._version) {
              serverOp = await updateEntityOrthophoto(siteId, op.id, { url: result.url }, op._version);
            } else {
              serverOp = await createEntityOrthophoto(siteId, { ...op, url: result.url });
            }
            updated.orthophotos[item.index]._version = serverOp._version;
            // Update local store state directly (NOT via updateOrthophoto action,
            // which would re-trigger entity API → double-write + version conflict)
            useProjectStore.setState((state) => ({
              data: {
                ...state.data,
                orthophotos: state.data.orthophotos.map((o) =>
                  o.id === op.id ? { ...o, url: result.url, _version: serverOp._version } : o
                ),
              },
            }));
          } catch (err) {
            console.warn(`[Sync] Entity orthophoto persist failed for ${op.id}:`, err);
          }
          break;
        }
        case 'photo': {
          // V4 P2.7 / F-04A — Indications are entity-managed; the blob save
          // strips them, so without an entity PATCH the new R2 URL never reaches
          // D1 and on reload the indication still references the relative path
          // ("photos/DJI_…JPG") → marker shows but the photo 404s. Here we only
          // rewrite the uploaded field (image / visualImage / zoomImage) in
          // memory and record it; the single per-indication PATCH happens after
          // the loop (see photoUrlRewrites pass) to avoid _version chaining.
          if (item.field) {
            updated.indications[item.index].properties[item.field] = result.url;
            const acc = photoUrlRewrites.get(item.index) ?? {};
            acc[item.field] = result.url;
            photoUrlRewrites.set(item.index, acc);
          }
          break;
        }
        case 'geo_photo':
          updated.geoPhotos[item.index].url = result.url;
          break;
        case 'layout_image': {
          // F-01A: write through to siteLayouts[index] and keep the legacy
          // singleton alias in sync. Fall back to mutating the legacy
          // singleton directly if the array isn't populated.
          if (updated.siteLayouts && updated.siteLayouts[item.index]?.pageImage) {
            updated.siteLayouts[item.index].pageImage!.url = result.url;
            if (item.index === 0 && updated.siteLayout?.pageImage) {
              updated.siteLayout.pageImage.url = result.url;
            }
          } else if (updated.siteLayout?.pageImage) {
            updated.siteLayout.pageImage.url = result.url;
          }
          break;
        }
        case 'layout_pdf': {
          if (updated.siteLayouts && updated.siteLayouts[item.index]) {
            updated.siteLayouts[item.index].pdfAssetUrl = result.url;
            if (item.index === 0 && updated.siteLayout) {
              updated.siteLayout.pdfAssetUrl = result.url;
            }
          } else if (updated.siteLayout) {
            updated.siteLayout.pdfAssetUrl = result.url;
          }
          break;
        }
      }
    } catch (err) {
      console.warn(`[Sync] Failed to upload ${item.name}:`, err);
      // Continue with other uploads
    }
  }

  // F-04A: persist photo-field URL rewrites to the entity table — ONE PATCH per
  // affected indication (not one per uploaded field) so image+visualImage+
  // zoomImage ride in a single properties write and don't chain _version → 412.
  for (const [index, rewrites] of photoUrlRewrites) {
    const ind = updated.indications[index];
    const indVersion = ind._version;
    if (!indVersion) {
      console.warn(`[Sync] No _version for indication ${ind.id} when persisting photo URL(s) — will be lost on reload`);
      continue;
    }
    try {
      const serverInd = await updateEntityIndication(
        siteId,
        ind.id,
        { properties: ind.properties as unknown as Record<string, unknown> },
        indVersion,
      );
      updated.indications[index]._version = serverInd._version;
      useProjectStore.setState((state) => ({
        data: {
          ...state.data,
          indications: state.data.indications.map((i) =>
            i.id === ind.id
              ? { ...i, properties: { ...i.properties, ...rewrites }, _version: serverInd._version }
              : i
          ),
        },
      }));
    } catch (err) {
      console.warn(`[Sync] Entity indication URL update failed for ${ind.id}:`, err);
    }
  }

  // Include monitoring data in the project save
  const monStore = useMonitoringStore.getState();
  if (monStore.projectConfig) updated.monitoringConfig = monStore.projectConfig;
  if (monStore.schema) updated.monitoringSchema = monStore.schema;
  if (monStore.uploadedSampleData) updated.monitoringSampleData = monStore.uploadedSampleData;
  // Include SLD diagrams
  const monExport = monStore.exportMonitoringConfig();
  if (monExport.sldDiagrams.length > 0) updated.sldDiagrams = monExport.sldDiagrams;

  // V4: Strip entities managed by entity API — they are the sole authority.
  const {
    indications: _strippedIndications,
    overlays: _strippedOverlays,
    orthophotos: _strippedOrthophotos,
    tasksData: _strippedTasks,
    ...blobWithoutEntities
  } = updated as unknown as Record<string, unknown>;
  const strippedCounts = {
    indications: Array.isArray(_strippedIndications) ? (_strippedIndications as unknown[]).length : 0,
    overlays: Array.isArray(_strippedOverlays) ? (_strippedOverlays as unknown[]).length : 0,
    orthophotos: Array.isArray(_strippedOrthophotos) ? (_strippedOrthophotos as unknown[]).length : 0,
    tasksData: _strippedTasks ? 1 : 0,
  };
  const strippedTotal = strippedCounts.indications + strippedCounts.overlays + strippedCounts.orthophotos + strippedCounts.tasksData;
  if (strippedTotal > 0) {
    console.log(`[Sync] V4: Stripped from blob — indications: ${strippedCounts.indications}, overlays: ${strippedCounts.overlays}, orthophotos: ${strippedCounts.orthophotos}, tasksData: ${strippedCounts.tasksData} (entity API is authority)`);
  }

  // Save the project JSON to D1 (without entity-managed data)
  onProgress?.(total, total, 'Saving project data...');
  const savePayload = JSON.stringify(blobWithoutEntities);
  console.log('[Sync] Saving JSON to D1, size:', savePayload.length, 'bytes');
  console.log('[Sync] Payload preview (first 500 chars):', savePayload.slice(0, 500));
  const currentVersion = useProjectStore.getState().meta.version;
  const saveResult = await saveProject(siteId, blobWithoutEntities as unknown as Record<string, unknown>, currentVersion);

  // Check for version conflict — surface via store, return local data unchanged
  if ('conflict' in saveResult) {
    console.error('[Sync] Version conflict — server:', saveResult.serverVersion, 'local:', currentVersion);
    useProjectStore.getState().setSaveStatus('conflict');
    return updated;
  }

  // Update meta.version from server response
  useProjectStore.getState().setMeta({ version: saveResult.version });
  console.log('[Sync] D1 save response:', saveResult);

  // Verification: read back from server to confirm data persisted
  onProgress?.(total, total, 'Verifying save...');
  try {
    const readBack = await fetchProject(siteId);
    const readBackSize = JSON.stringify(readBack.data || {}).length;
    console.log('[Sync] Verification read-back size:', readBackSize, 'bytes');
    console.log('[Sync] Verification updated_at:', readBack.updated_at);
    if (readBackSize < 10) {
      const msg = `Save verification FAILED: sent ${savePayload.length} bytes but read back only ${readBackSize} bytes. Data may not have persisted.`;
      console.error('[Sync]', msg);
      throw new Error(msg);
    }
    console.log('[Sync] Verification PASSED — data persisted successfully');
  } catch (verifyErr) {
    if (verifyErr instanceof Error && verifyErr.message.startsWith('Save verification')) {
      throw verifyErr; // Re-throw our own verification error
    }
    console.warn('[Sync] Verification fetch failed (non-critical):', verifyErr);
  }

  // ─── Post-save: Upload source files and trigger tile generation ────────
  const sourceFiles = getAllSourceFiles();
  if (sourceFiles.size > 0) {
    console.log(`[Sync] ${sourceFiles.size} source file(s) pending for tile generation`);

    for (const [layerId, entry] of sourceFiles.entries()) {
      // Find the orthophoto in the updated data
      const ortho = updated.orthophotos.find((o) => o.id === layerId);
      if (!ortho) continue;

      // Skip if already has tiles
      if (ortho.tilesetId && ortho.tileStatus === 'ready') {
        removeSourceFile(layerId);
        continue;
      }

      try {
        // If source already uploaded (e.g., during server preview), reuse it
        let sourceR2Key = ortho.sourceR2Key;
        if (!sourceR2Key) {
          // Use uploadLargeAsset — orthophoto sources (TIFFs especially) often
          // exceed the 30-50 MB range where a single-shot multipart POST is
          // unreliable on slower links. Files over MULTIPART_THRESHOLD are
          // streamed as chunks; smaller files fall back to uploadAsset.
          onProgress?.(total, total, `Uploading source: ${entry.file.name}...`);
          const ext = entry.file.name.split('.').pop() || 'jpg';
          const sourceResult = await uploadLargeAsset(
            entry.file,
            siteId,
            'orthophoto_source',
            `${layerId}_source.${ext}`,
            (loaded, totalBytes) => {
              const pct = Math.round((loaded / totalBytes) * 100);
              onProgress?.(total, total, `Uploading source: ${entry.file.name} (${pct}%)...`);
            },
          );
          sourceR2Key = sourceResult.r2Key;
        }

        // Store sourceR2Key on the orthophoto
        ortho.sourceR2Key = sourceR2Key;

        // Trigger tile generation
        onProgress?.(total, total, `Generating tiles: ${ortho.name}...`);
        const corners = ortho.coordinates.map((c) => [c[0], c[1]]);
        const tileResult = await requestTileGeneration(
          siteId,
          'orthophoto',
          layerId,
          sourceR2Key,
          ortho.bounds,
          corners,
          entry.width,
          entry.height,
          ortho.bgRemoved ?? false
        );

        ortho.tilesetId = tileResult.tilesetId;
        ortho.tileStatus = 'generating';
        console.log(`[Sync] Tile generation started for ${ortho.name}: ${tileResult.tilesetId}`);

        // Persist tile metadata to the entity table so a page reload sees
        // the new tilesetId/tileStatus. Without this PATCH the orthophoto
        // row keeps tileset_id=NULL → no HD layer rendered, no poller fires
        // (poller is gated on tileStatus === 'generating' && tilesetId).
        // Use the freshest version — URL-upload PATCH above bumped it.
        const tileVersion = ortho._version;
        if (tileVersion) {
          try {
            const serverOp = await updateEntityOrthophoto(
              siteId,
              layerId,
              {
                tilesetId: tileResult.tilesetId,
                tileStatus: 'generating',
                sourceR2Key,
              },
              tileVersion,
            );
            ortho._version = serverOp._version;
          } catch (err) {
            console.warn(`[Sync] Entity tile-info PATCH failed for ${ortho.name}:`, err);
          }
        } else {
          // Should not happen — by now the URL persistence (create or PATCH)
          // assigned a version. Surface loudly so future regressions are
          // caught instead of silently dropping tile state.
          console.warn(`[Sync] No _version for ${layerId} when persisting tile info — tile state will not survive a reload`);
        }

        // Update local store state directly with tile info.
        // Use setState (NOT updateOrthophoto action) to avoid re-triggering entity API.
        useProjectStore.setState((state) => ({
          data: {
            ...state.data,
            orthophotos: state.data.orthophotos.map((o) =>
              o.id === layerId
                ? { ...o, tilesetId: tileResult.tilesetId, tileStatus: 'generating' as const, sourceR2Key, _version: ortho._version }
                : o
            ),
          },
        }));

        removeSourceFile(layerId);
      } catch (err) {
        console.warn(`[Sync] Source upload/tile gen failed for ${ortho.name}:`, err);
      }
    }

    // Re-save project blob (without entity-managed data) — tile info is also
    // persisted to entity table via entity API.
    try {
      const tileVersion = useProjectStore.getState().meta.version;
      const {
        indications: _tileStripInd,
        overlays: _tileStripOv,
        orthophotos: _tileStripOp,
        tasksData: _tileStripTasks,
        ...tileBlobWithoutEntities
      } = updated as unknown as Record<string, unknown>;
      const tileSaveResult = await saveProject(siteId, tileBlobWithoutEntities as unknown as Record<string, unknown>, tileVersion);
      if (!('conflict' in tileSaveResult)) {
        useProjectStore.getState().setMeta({ version: tileSaveResult.version });
      }
      console.log('[Sync] Re-saved project blob (entities excluded)');
    } catch (err) {
      console.warn('[Sync] Re-save with tile info failed (non-critical):', err);
    }
  }

  // Re-read entity-managed fields from the store before returning. They may
  // have been updated asynchronously during this sync — most commonly by
  // useTileStatusPoller flipping tileStatus from 'generating' to 'ready' the
  // moment Lambda finishes (which can happen mid-sync if tiles cache fast).
  // Returning the cloned `updated` data verbatim would let the caller's
  // setData() clobber those concurrent updates back to stale values, which
  // re-arms the poller and triggers a 412/409 on the next PATCH (stale
  // _version → conflict → reload-from-server → noisy console).
  const finalState = useProjectStore.getState().data;
  return {
    ...updated,
    indications: finalState.indications,
    overlays: finalState.overlays,
    orthophotos: finalState.orthophotos,
  };
}
